/**
 * @module agent/adapters/services/CacheSyncStrategy
 *
 * Handles session caching and synchronization strategy.
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * Responsibilities:
 * - Cache-first session retrieval
 * - Background refresh from backend
 * - Orphan message cleanup
 * - Preserve locally-created sessions during sync
 */

/**
 * Session caching and sync strategy.
 */
class CacheSyncStrategy {
  /**
   * @param {Object} deps - Dependencies
   * @param {Object} deps.sessionManager - SessionManager instance
   * @param {Object} deps.messageManager - MessageManager instance
   * @param {Object} deps.backendSync - BackendSyncService instance
   * @param {Function} deps.listSessions - API function to list sessions
   * @param {Function} [deps.onSessionsRefreshed] - Notified once per getAllSessions() call when the
   *   backend list has settled. See _notifySessionsRefreshed for the contract.
   * @throws {TypeError} If required dependencies are missing
   */
  constructor(deps) {
    if (!deps || typeof deps !== 'object') {
      throw new TypeError('CacheSyncStrategy: dependencies object required');
    }
    if (!deps.sessionManager) {
      throw new TypeError('CacheSyncStrategy: sessionManager is required');
    }
    if (!deps.messageManager) {
      throw new TypeError('CacheSyncStrategy: messageManager is required');
    }
    if (!deps.backendSync) {
      throw new TypeError('CacheSyncStrategy: backendSync is required');
    }
    if (typeof deps.listSessions !== 'function') {
      throw new TypeError('CacheSyncStrategy: listSessions function is required');
    }

    this._sessionManager = deps.sessionManager;
    this._messageManager = deps.messageManager;
    this._backendSync = deps.backendSync;
    this._listSessions = deps.listSessions;

    // Optional callback for orphan cleanup notifications
    this._onOrphanCleanup = deps.onOrphanCleanup || null;

    // TD-2571: Optional callback fired once per getAllSessions() call when the backend list has
    // settled. Consumers use it to distinguish "the backend confirmed this session is gone" from
    // "we haven't heard back yet" — without it a cache-first hit looks identical to a confirmed
    // absence, which is what left a missing thread stuck on "Disconnected" forever.
    this._onSessionsRefreshed = deps.onSessionsRefreshed || null;

    // Track recent mutations to skip background refresh
    this._recentMutation = false;
    this._mutationTimeoutId = null;

    // AbortController for cancellable background refresh
    this._refreshAbortController = null;
  }

  /**
   * TD-2571: Announce that the backend session list has settled.
   *
   * Contract: fires exactly once per getAllSessions() call, on every path — after the blocking
   * fetch when there is no cache, when the background refresh settles on a cache-first hit, or
   * immediately on a cache-first hit that skips the refresh. Fires on failure as well as success
   * so a swallowed network error still resolves the UI instead of hanging it; `ok` tells the two
   * apart. Deliberately NOT fired for an aborted refresh — that one has a successor in flight.
   *
   * @param {Array} sessions - Sessions as currently known
   * @param {boolean} ok - Whether the backend was actually reached
   * @private
   */
  _notifySessionsRefreshed(sessions, ok) {
    if (!this._onSessionsRefreshed) return;

    try {
      this._onSessionsRefreshed({ sessions, ok });
    } catch (error) {
      console.error('❌ CacheSyncStrategy: onSessionsRefreshed callback threw:', error);
    }
  }

  /**
   * Get all sessions with cache-first strategy.
   *
   * @param {Object} options - Options
   * @param {boolean} [options.cacheFirst=true] - Return cache immediately if available
   * @param {boolean} [options.skipBackgroundRefresh=false] - Skip background refresh
   * @returns {Promise<Array>} Sessions sorted by lastActivity (newest first)
   */
  async getAllSessions(options = {}) {
    const { cacheFirst = true, skipBackgroundRefresh = false } = options;

    // Cache-first: return localStorage data immediately if available
    if (cacheFirst && this._sessionManager.count() > 0) {
      const cachedSessions = this._sessionManager.getAll();

      // Trigger background refresh (non-blocking) unless disabled
      if (!skipBackgroundRefresh && !this._recentMutation) {
        this.refreshInBackground();
      } else {
        // TD-2571: no refresh will run, so this cache read is as settled as it gets. Announce it
        // now — otherwise callers waiting on the settle event would wait forever.
        this._notifySessionsRefreshed(cachedSessions, true);
      }

      return cachedSessions;
    }

    // No cache - fetch from backend (blocking)
    return this._fetchAndStoreSessions();
  }

  /**
   * Fetch sessions from backend and store locally.
   *
   * @returns {Promise<Array>} Transformed and sorted sessions
   * @private
   */
  async _fetchAndStoreSessions() {
    try {
      const backendSessions = await this._listSessions();

      // Transform backend format to adapter format
      const transformedSessions = backendSessions.map((s) =>
        this._backendSync.transformBackendSession(s)
      );

      // Clear and repopulate (removes deleted sessions)
      this._sessionManager.clear();
      transformedSessions.forEach((session) => {
        this._sessionManager.set(session.id, session);
      });
      this._sessionManager.save();

      // Clean up orphaned messages
      const validSessionIds = new Set(transformedSessions.map((s) => s.id));
      this.cleanupOrphanedMessages(validSessionIds);

      // Sort by lastActivity (newest first)
      transformedSessions.sort((a, b) => b.lastActivity - a.lastActivity);

      this._notifySessionsRefreshed(transformedSessions, true);
      return transformedSessions;
    } catch (error) {
      console.error('❌ CacheSyncStrategy: Error fetching sessions:', error);
      // Fallback to local sessions on error
      const localSessions = this._sessionManager.getAll();
      this._notifySessionsRefreshed(localSessions, false);
      return localSessions;
    }
  }

  /**
   * Refresh sessions from backend in background (non-blocking).
   * Preserves locally-created sessions during refresh.
   * Supports cancellation via AbortController.
   */
  async refreshInBackground() {
    // Cancel previous refresh if still running
    if (this._refreshAbortController) {
      this._refreshAbortController.abort();
    }

    this._refreshAbortController = new AbortController();
    const signal = this._refreshAbortController.signal;

    try {
      // Check abort before expensive operations
      if (signal.aborted) return;

      const backendSessions = await this._listSessions();

      if (signal.aborted) return;

      const transformedSessions = backendSessions.map((s) =>
        this._backendSync.transformBackendSession(s)
      );

      if (signal.aborted) return;

      // Snapshot entries to prevent concurrent modification (Fix #3)
      const sessionEntries = Array.from(this._sessionManager.entries());

      // Preserve locally-created sessions
      const locallyCreated = new Map();
      for (const [id, session] of sessionEntries) {
        if (session._justCreated || session._awaitingFirstMessage) {
          locallyCreated.set(id, session);
        }
      }

      if (signal.aborted) return;

      // Clear and repopulate with backend sessions
      this._sessionManager.clear();
      transformedSessions.forEach((s) => this._sessionManager.set(s.id, s));

      // Restore locally-created sessions
      for (const [id, session] of locallyCreated) {
        this._sessionManager.set(id, session);
      }
      this._sessionManager.save();

      // Clean up orphaned messages
      const validIds = new Set([...transformedSessions.map((s) => s.id), ...locallyCreated.keys()]);
      this.cleanupOrphanedMessages(validIds);

      this._notifySessionsRefreshed(this._sessionManager.getAll(), true);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 CacheSyncStrategy: Background refresh aborted');
        return;
      }
      console.error('❌ CacheSyncStrategy: Background refresh failed:', error);
      // Silent failure for background operations, but the settle event still fires with ok=false —
      // a caller waiting on it must not be left hanging by an error we chose to swallow (TD-2571).
      this._notifySessionsRefreshed(this._sessionManager.getAll(), false);
    } finally {
      this._refreshAbortController = null;
    }
  }

  /**
   * Remove messages for sessions that no longer exist.
   *
   * @param {Set<string>} validSessionIds - Set of valid session IDs
   * @returns {string[]} Array of orphaned session IDs that were cleaned up
   */
  cleanupOrphanedMessages(validSessionIds) {
    const orphanedIds = [];

    for (const sessionId of this._messageManager.sessionIds()) {
      if (!validSessionIds.has(sessionId)) {
        this._messageManager.deleteForSession(sessionId);
        orphanedIds.push(sessionId);
      }
    }

    // Notify via callback if provided
    if (this._onOrphanCleanup && orphanedIds.length > 0) {
      this._onOrphanCleanup(orphanedIds);
    }

    // Log cleanup summary (not per-session)
    if (orphanedIds.length > 0) {
      console.log(
        `🧹 CacheSyncStrategy: Removed orphaned messages for ${orphanedIds.length} session(s)`
      );
    }

    return orphanedIds;
  }

  /**
   * Mark that a recent mutation occurred (skip background refresh).
   * Uses clearable timeout to prevent race conditions with rapid mutations.
   */
  markRecentMutation() {
    this._recentMutation = true;

    // Clear existing timeout if present (prevents race condition)
    if (this._mutationTimeoutId) {
      clearTimeout(this._mutationTimeoutId);
    }

    // Set new timeout
    this._mutationTimeoutId = setTimeout(() => {
      this._recentMutation = false;
      this._mutationTimeoutId = null;
    }, 5000);
  }

  /**
   * Clear recent mutation flag.
   */
  clearRecentMutation() {
    this._recentMutation = false;
    if (this._mutationTimeoutId) {
      clearTimeout(this._mutationTimeoutId);
      this._mutationTimeoutId = null;
    }
  }

  /**
   * Clean up resources (timers, abort controllers).
   */
  destroy() {
    // Clear mutation timeout
    if (this._mutationTimeoutId) {
      clearTimeout(this._mutationTimeoutId);
      this._mutationTimeoutId = null;
    }

    // Abort any in-flight refresh
    if (this._refreshAbortController) {
      this._refreshAbortController.abort();
      this._refreshAbortController = null;
    }

    this._recentMutation = false;
  }
}

export default CacheSyncStrategy;
